/** @jsxImportSource @emotion/react */

import { useSelector } from 'react-redux';
import { useState } from 'react';
import { Formik } from 'formik';
import { object, string as yupString, array as yupArray } from 'yup';
import {
  ButtonStyled,
  DropDownStyled,
  CheckboxSetStyled,
  CheckboxStyled,
  TextFieldStyled,
  TextStyled
} from '@nike/nike-design-system-components';
import { Search, InfoCircle, Close } from '@nike/nike-design-system-icons';
import { nike } from '@nike/nike-design-tokens';
import { Tooltip } from '@nike/eds';

import { TranslatedCopy } from '../../intl/TranslatedCopy';

import { usersActions } from './actions';
import { requestsSelectors } from '../../requests/reducer';
import { usersSelectors } from './reducer';
import { getCurrentDepartments } from '../../user/reducer';
import { useDispatchWithProgress, useDispatchWithProgressAndSuccess } from '../../hooks/useDispatchWithProgress';

const UserForm = () => {
  const updateUser = useDispatchWithProgressAndSuccess(usersActions.updateUser);
  const toggleUserStatus = useDispatchWithProgress(usersActions.toggleUserStatus);

  const usersActiveReview = useSelector(usersSelectors.activeReview);
  const selectedAccountNumber = useSelector(state => state.user.get('userAccount')?.number);
  const roles = useSelector(usersSelectors.roles);
  const departments = useSelector(state => getCurrentDepartments(state, selectedAccountNumber));
  const selectedUserDepartments = useSelector(usersSelectors.selectedUserDepartments);
  const selectedUserId = useSelector(usersSelectors.selectedUserId);
  const selectedUserRole = useSelector(usersSelectors.selectedUserRole);
  const selectedUserIsActive = useSelector(usersSelectors.selectedUserIsActive);
  const updateUserError = useSelector(requestsSelectors.updateUserError);
  const updateUserInProgress = useSelector(requestsSelectors.updateUserInProgress);
  const toggleUserStatusInProgress = useSelector(requestsSelectors.toggleUserStatusInProgress);

  const [deptSearchInput, setDeptSearchInput] = useState(null);

  return (
    <Formik
      initialValues={{
        departmentsList: departments,
        departments: [...selectedUserDepartments],
        role: selectedUserRole
      }}
      validationSchema={object().shape({
        departments: yupArray().min(1),
        role: yupString().required().nullable()
      })}
      onSubmit={(values, actions) => {
        updateUser({
          userId: selectedUserId,
          accountNumber: selectedAccountNumber,
          departmentNumbers: values.departments,
          role: values.role,
          activeOnAccount: usersActiveReview
        });

        actions.resetForm();
      }}
    >
      {formik => (
        <form
          id='userUpdate'
          css={{
            marginTop: '16px',
            marginBottom: '12px',
            opacity: updateUserInProgress || updateUserError || toggleUserStatusInProgress ? 0.1 : 1
          }}
          onSubmit={formik.handleSubmit}
        >
          <div
            css={{
              display: 'block',
              width: '100%',
              alignItems: 'center',
              '@media (min-width: 769px)': {
                display: 'flex'
              }
            }}
          >
            <div css={{ flex: 1, display: 'flex' }}>
              <DropDownStyled
                id='role'
                css={{ width: '325px' }}
                error={formik.touched.role && formik.errors.role}
                errorMessage={formik.errors.role}
                label={new TranslatedCopy({ id: 'userManagement.addUser.label.role' }).toString()}
                name='role'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                options={roles.map(role => {
                  return {
                    label: new TranslatedCopy({ id: `userManagement.roles.${role.toLowerCase()}` }).toString(),
                    value: role
                  };
                })}
                value={formik.values.role}
              />
              <div css={{ alignSelf: 'center', marginLeft: '10px', color: 'white' }}>
                <Tooltip
                  id='rolesTooltip'
                  bodySlot={
                    <div css={{ lineHeight: 'initial' }}>
                      <TextStyled appearance='body3' bold css={{ paddingBottom: '14px' }}>
                        <TranslatedCopy id='userManagement.roles.tooltip.title' />
                      </TextStyled>
                      {roles.map(role => (
                        <div css={{ paddingBottom: '10px' }}>
                          <TextStyled display='inline' appearance='body3Strong'>
                            <TranslatedCopy id={`userManagement.roles.${role.toLowerCase()}`} />:{' '}
                          </TextStyled>
                          <TextStyled display='inline' appearance='body3'>
                            <TranslatedCopy id={`userManagement.roles.${role.toLowerCase()}.description`} />
                          </TextStyled>
                        </div>
                      ))}
                    </div>
                  }
                  isDark
                  enableFocus
                >
                  <InfoCircle />
                </Tooltip>
              </div>
            </div>
            <div
              css={{
                flex: 1,
                marginBottom: '10px',
                '@media (max-width: 769px)': {
                  padding: '0 2px'
                }
              }}
            >
              <ButtonStyled
                appearance='secondary'
                disabled={toggleUserStatusInProgress}
                onClick={
                  () =>
                    toggleUserStatus({
                      userId: selectedUserId,
                      accountNumber: selectedAccountNumber,
                      isActive: selectedUserIsActive
                    })
                  // AFTER UAT, REMOVE OR UNCOMMENT BASED ON FEEDBACK
                  // dispatch({ type: usersActions.removeUserFromTable.name, payload: selectedUserId });
                }
              >
                <TranslatedCopy
                  id={`userManagement.details.actions.${selectedUserIsActive ? 'active' : 'deactivated'}`}
                />
              </ButtonStyled>
            </div>
          </div>
          <div
            css={{
              paddingTop: '5px',
              paddingBottom: '10px',
              borderBottom: `1px solid ${nike.podiumCdsColorBorderPrimary}`
            }}
          >
            <TextStyled appearance='title4'>
              <TranslatedCopy id='global.departments' />
            </TextStyled>
          </div>
          <div css={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
            <TextFieldStyled
              id='departmentFilter'
              css={{
                marginTop: '12px',
                minHeight: '68px',
                width: '100%',
                '@media (min-width: 769px)': {
                  width: '325px'
                }
              }}
              label={new TranslatedCopy({ id: 'global.search' }).toString()}
              name='departmentFilter'
              onChange={event => {
                formik.setFieldValue(
                  'departmentsList',
                  departments.filter(department =>
                    department.customerDepartmentName.toLowerCase().includes(event.target.value.toLowerCase())
                  )
                );
                setDeptSearchInput(event.target.value);
              }}
              value={deptSearchInput}
              PrefixIcon={<Search />}
              {...(deptSearchInput && {
                TrailingIcon: <Close />
              })}
              onIconClick={() => {
                setDeptSearchInput(null);
                formik.setFieldValue('departmentsList', departments);
              }}
            />
            <div css={{ display: 'flex', flexDirection: 'row', marginTop: '14px', marginBottom: '3px' }}>
              <ButtonStyled
                appearance='text'
                css={{
                  display: 'block',
                  borderBottomWidth: '2px',
                  borderBottomStyle: 'solid'
                }}
                onClick={() => {
                  formik.setFieldValue(
                    'departments',
                    formik.values.departmentsList.map(department => department.customerDepartmentCode)
                  );
                }}
                type='button'
              >
                <TranslatedCopy id='cart.header.button.selectAll' />
              </ButtonStyled>
              <ButtonStyled
                appearance='text'
                css={{
                  display: 'block',
                  marginLeft: '16px',
                  borderBottomWidth: '2px',
                  borderBottomStyle: 'solid'
                }}
                onClick={() => {
                  formik.setFieldValue('departments', []);
                }}
                type='button'
              >
                <TranslatedCopy id='global.clear' />
              </ButtonStyled>
            </div>
          </div>
          <CheckboxSetStyled
            id='departments'
            css={{
              '.ndsc-selection-children-container': {
                flexDirection: 'row',
                flexWrap: 'wrap'
              },
              '.ndsc-selection-children-container > div': {
                marginLeft: 0,
                marginTop: '12px',
                width: '50%',
                '@media (min-width: 769px)': {
                  width: '33%'
                }
              }
            }}
            error={formik.touched.departments && formik.errors.departments}
            errorMessage={formik.errors.departments}
            name='departments'
            onChange={event => {
              formik.setFieldValue(
                'departments',
                event.target.checked
                  ? [...formik.values.departments, event.target.value]
                  : formik.values.departments.filter(department => department !== event.target.value)
              );
            }}
            selectedValues={formik.values.departments}
          >
            {formik.values.departmentsList.map(department => (
              <CheckboxStyled
                key={department.customerDepartmentCode}
                id={department.customerDepartmentCode}
                label={department.customerDepartmentName}
                value={department.customerDepartmentCode}
              />
            ))}
          </CheckboxSetStyled>
        </form>
      )}
    </Formik>
  );
};

export default UserForm;
